import React, { useRef, useState, useEffect } from "react";
import { RxCross2 } from "react-icons/rx";
import { RiUploadCloud2Line } from "react-icons/ri";
import { MdOutlineCloudDone } from "react-icons/md";
import { RiDeleteBin5Line } from "react-icons/ri";
import { FaRegFileAlt } from "react-icons/fa";
import { toast } from "react-toastify";
import '../../components/VerifyAccountModal/VerifyAccountModal.css';
import { BASE_URL_API } from "../../API/Api";
import { getAuthToken, setAuthToken, setAuthData, getUserData } from "../../utils/auth";
import { useNavigate } from 'react-router-dom';
const VerifyAccountModal = ({ isOpen, onClose }) => {
    const msmeFileInputRef = useRef(null);
    const panFileInputRef = useRef(null);
    const gstFileInputRef = useRef(null);

    const [msmeFile, setMsmeFile] = useState(null);
    const [panFile, setPanFile] = useState(null);
    const [gstFile, setGstFile] = useState(null);
    const [gstin, setGstin] = useState("");
    const [msmeNumber, setMsmeNumber] = useState("");
    const [panNumber, setPanNumber] = useState("");
    const [gstNumber, setGstNumber] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isFetchingGst, setIsFetchingGst] = useState(false);
    const [businessName, setBusinessName] = useState("");
    const [businessAddress, setBusinessAddress] = useState("");
    const [gstData, setGstData] = useState(null);
    const [isGstFetched, setIsGstFetched] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const userData = getUserData();

        if (userData) {
            if (userData.GSTIN) {
                setGstin(userData.GSTIN);
                setGstNumber(userData.GSTIN);
                setIsGstFetched(true);
            }
            if (userData.pan_card) {
                setPanNumber(userData.pan_card);
            }
        }
    }, []);


    if (!isOpen) return null;

    const resetForm = () => {
        setMsmeFile(null);
        setPanFile(null);
        setGstFile(null);
        setGstin("");
        setMsmeNumber("");
        setPanNumber("");
        setGstNumber("");
        setBusinessName("");
        setBusinessAddress("");
        setGstData(null);
        setIsGstFetched(false);
        if (msmeFileInputRef.current) msmeFileInputRef.current.value = "";
        if (panFileInputRef.current) panFileInputRef.current.value = "";
        if (gstFileInputRef.current) gstFileInputRef.current.value = "";
    };

    const handleFileChange = (e, type) => {
        const file = e.target.files[0];
        if (file) {
            if (type === "msme") setMsmeFile(file);
            if (type === "pan") setPanFile(file);
            if (type === "gst") setGstFile(file);
        }
    };

    const handleFileDelete = (type) => {
        if (type === "msme") {
            setMsmeFile(null);
            msmeFileInputRef.current.value = "";
        }
        if (type === "pan") {
            setPanFile(null);
            panFileInputRef.current.value = "";
        }
        if (type === "gst") {
            setGstFile(null);
            gstFileInputRef.current.value = "";
        }
    };

    // Validation functions
    const validateMsmeNumber = (number) => {
        return /^[0-9]{12}$/.test(number);
    };

    const validatePanNumber = (number) => {
        return /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(number);
    };

    const validateGstNumber = (number) => {
        return /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(number);
    };

    const validateFile = (file, type) => {
        if (!file) {
            toast.error(`Please upload ${type}`, { position: "top-right" });
            return false;
        }

        const validTypes = ['application/pdf', 'image/jpeg', 'image/png'];
        if (!validTypes.includes(file.type)) {
            toast.error(`Invalid file type for ${type}. Please upload PDF, JPEG, or PNG`, { position: "top-right" });
            return false;
        }

        if (file.size > 5 * 1024 * 1024) {
            toast.error(`File size should be less than 5MB for ${type}`, { position: "top-right" });
            return false;
        }

        return true;
    };

    const fetchGstDetails = async () => {
        if (!gstin) {
            toast.error("Please enter GSTIN number", { position: "top-right" });
            return;
        }

        if (!validateGstNumber(gstin)) {
            toast.error("Please enter a valid GSTIN format (22AAAAA0000A1Z5)", { position: "top-right" });
            return;
        }

        setIsFetchingGst(true);

        try {
            const token = getAuthToken();
            const response = await fetch(`${BASE_URL_API}api/gst/details?gstin=${gstin}`, {
                method: "POST",
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            const data = await response.json();

            if (response.ok && data?.status && data?.data?.flag) {
                const gstDetails = data.data.data;
                setGstData(gstDetails);
                setBusinessName(gstDetails.lgnm || gstDetails.tradeNam || "");

                const address = gstDetails.pradr?.adr ||
                    `${gstDetails.pradr?.addr?.st || ''}, ${gstDetails.pradr?.addr?.loc || ''}, 
                    ${gstDetails.pradr?.addr?.dst || ''}, ${gstDetails.pradr?.addr?.stcd || ''} - 
                    ${gstDetails.pradr?.addr?.pncd || ''}`;
                setBusinessAddress(address);

                const panFromGstin = gstin.slice(2, 12);
                setPanNumber(panFromGstin);
                setGstNumber(gstin);
                setIsGstFetched(true);

                toast.success("GST details fetched successfully!", {
                    position: "top-right",
                    autoClose: 3000,
                });
            } else {
                if (response.status === 401) {
                    handleUnauthorized();
                    return;
                }
                const errorMsg = data?.data?.message || "Failed to fetch GST details";
                toast.error(errorMsg, {
                    position: "top-right",
                    autoClose: 5000,
                });
            }
        } catch (error) {
            console.error("Error fetching GST details:", error);
            toast.error("An error occurred while fetching GST details", {
                position: "top-right",
                autoClose: 5000,
            });
        } finally {
            setIsFetchingGst(false);
        }
    };

    const validateForm = () => {
        if (!isGstFetched) {
            toast.error("Please fetch GST details first", { position: "top-right" });
            return false;
        }

        // MSME validation
        // if (!msmeNumber) {
        //     toast.error("Please enter MSME number", { position: "top-right" });
        //     return false;
        // }
        // if (!validateMsmeNumber(msmeNumber)) {
        //     toast.error("MSME number should be exactly 12 digits", { position: "top-right" });
        //     return false;
        // }
        // if (!validateFile(msmeFile, "MSME certificate")) {
        //     return false;
        // }

        // PAN validation
        if (!panNumber) {
            toast.error("Please enter PAN number", { position: "top-right" });
            return false;
        }
        if (!validatePanNumber(panNumber)) {
            toast.error("Please enter a valid PAN format (e.g., AAAAA0000A)", { position: "top-right" });
            return false;
        }
        if (!validateFile(panFile, "PAN certificate")) {
            return false;
        }

        // GST certificate validation
        if (!validateFile(gstFile, "GST certificate")) {
            return false;
        }

        return true;
    };

    const handleUnauthorized = () => {
        toast.error("Session expired. Please login again.", {
            position: "top-right",
            autoClose: 5000,
        });
        setAuthToken(null);
        setAuthData(null);
        onClose();
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        setIsLoading(true);

        try {
            const token = getAuthToken();
            const formData = new FormData();
            formData.append("gstin", gstin);
            formData.append("business_name", businessName);
            formData.append("business_address", businessAddress);
            formData.append("msme_number", msmeNumber);
            formData.append("msme_certificate", msmeFile);
            formData.append("pan_number", panNumber);
            formData.append("pan_certificate", panFile);
            formData.append("gst_certificate", gstFile);

            if (gstData) {
                formData.append("registration_date", gstData.rgdt || "");
                formData.append("business_type", gstData.ctb || "");
                formData.append("gst_status", gstData.sts || "");
            }

            const response = await fetch(`${BASE_URL_API}api/user/verify`, {
                method: "POST",
                body: formData,
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            const data = await response.json();

            if (response.ok) {
                toast.success(data.message || "Account verification submitted successfully!", {
                    position: "top-right",
                    autoClose: 3000,
                });
                resetForm();

                onClose();
                setTimeout(() => {
                    navigate("/user-details");
                }, 1000);

            } else {
                if (response.status === 401) {
                    handleUnauthorized();
                    return;
                }
                toast.error(data.message || "Verification failed. Please try again.", {
                    position: "top-right",
                    autoClose: 5000,
                });
            }
        } catch (error) {
            console.error("Verification error:", error);
            toast.error("An error occurred. Please try again.", {
                position: "top-right",
                autoClose: 5000,
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="verificationModalOverlay" onClick={(e) => {
            if (e.target === e.currentTarget) {
                resetForm();
                onClose();
            }
        }}>
            <div className="verificationModal">
                <h2 className="verificationTitle">Verify Your Account</h2>
                <p>Fill details & upload certificates below*</p>

                <div className="inputGroup">
                    <label>GSTIN*</label>
                    <input
                        type="text"
                        placeholder="Enter GSTIN (22AAAAA0000A1Z5)"
                        value={gstin}
                        onChange={(e) => setGstin(e.target.value.toUpperCase())}
                        disabled={isGstFetched}
                        maxLength={15}
                    />
                    <button
                        onClick={fetchGstDetails}
                        disabled={isFetchingGst || isGstFetched}
                        className="fetchDetailsBtn"
                    >
                        {isFetchingGst ? "Fetching..." :
                            isGstFetched ? "Details Fetched" : "Fetch Details"}
                    </button>
                </div>



                <h3 className="businessDetailsTitle">Business documents</h3>

                <div className="businessDetails">
                    <div className="inputGroup">
                        <label>MSME Number*</label>
                        <div className="fileInputWrapper">
                            <input
                                type="text"
                                placeholder="Enter 12-digit MSME Number"
                                value={msmeNumber}
                                onChange={(e) => {
                                    const value = e.target.value.replace(/\D/g, '').slice(0, 12);
                                    setMsmeNumber(value);
                                }}

                            />
                            <div className="fileUploadArea">
                                {!msmeFile ? (
                                    <button
                                        onClick={() => msmeFileInputRef.current.click()}
                                        disabled={!isGstFetched}
                                    >
                                        <RiUploadCloud2Line /> Upload
                                    </button>
                                ) : (
                                    <div className="fileInfo">
                                        <div className="fileDetails">
                                            <FaRegFileAlt />
                                            <span>
                                                {msmeFile.name.length > 8
                                                    ? `${msmeFile.name.substring(0, 8)}...`
                                                    : msmeFile.name}
                                            </span>
                                        </div>
                                        <MdOutlineCloudDone className="uploadSuccessIcon" />
                                        <RiDeleteBin5Line
                                            className="deleteIcon deleteIconRed"
                                            onClick={() => handleFileDelete("msme")}
                                        />
                                    </div>
                                )}
                            </div>
                            <input
                                type="file"
                                ref={msmeFileInputRef}
                                style={{ display: 'none' }}
                                onChange={(e) => handleFileChange(e, "msme")}
                                disabled={!isGstFetched}
                                accept=".pdf,.jpg,.jpeg,.png"
                            />
                        </div>
                    </div>

                    <div className="inputGroup">
                        <label>PAN of company*</label>
                        <div className="fileInputWrapper">
                            <input
                                type="text"
                                placeholder="Enter PAN (AAAAA0000A)"
                                value={panNumber}
                                onChange={(e) => {
                                    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                                    setPanNumber(value.slice(0, 10));
                                }}
                            />
                            <div className="fileUploadArea">
                                {!panFile ? (
                                    <button
                                        onClick={() => panFileInputRef.current.click()}
                                        disabled={!isGstFetched}
                                    >
                                        <RiUploadCloud2Line /> Upload
                                    </button>
                                ) : (
                                    <div className="fileInfo">
                                        <div className="fileDetails">
                                            <FaRegFileAlt />
                                            <span>
                                                {panFile.name.length > 8
                                                    ? `${panFile.name.substring(0, 8)}...`
                                                    : panFile.name}
                                            </span>
                                        </div>
                                        <MdOutlineCloudDone className="uploadSuccessIcon" />
                                        <RiDeleteBin5Line
                                            className="deleteIcon deleteIconRed"
                                            onClick={() => handleFileDelete("pan")}
                                        />
                                    </div>
                                )}
                            </div>
                            <input
                                type="file"
                                ref={panFileInputRef}
                                style={{ display: 'none' }}
                                onChange={(e) => handleFileChange(e, "pan")}
                                disabled={!isGstFetched}
                                accept=".pdf,.jpg,.jpeg,.png"
                            />
                        </div>
                    </div>
                </div>

                <div className="inputGroup ">
                    <label>GST Certificate*</label>
                    <div className="fileInputWrapper">
                        <input
                            type="text"
                            placeholder="GST Number"
                            value={gstNumber}
                            readOnly
                        />
                        <div className="fileUploadArea">
                            {!gstFile ? (
                                <button
                                    onClick={() => gstFileInputRef.current.click()}
                                    disabled={!isGstFetched}
                                >
                                    <RiUploadCloud2Line /> Upload
                                </button>
                            ) : (
                                <div className="fileInfo">
                                    <div className="fileDetails">
                                        <FaRegFileAlt />
                                        <span>
                                            {gstFile.name.length > 8
                                                ? `${gstFile.name.substring(0, 8)}...`
                                                : gstFile.name}
                                        </span>
                                    </div>
                                    <MdOutlineCloudDone className="uploadSuccessIcon" />
                                    <RiDeleteBin5Line
                                        className="deleteIcon deleteIconRed"
                                        onClick={() => handleFileDelete("gst")}
                                    />
                                </div>
                            )}
                        </div>
                        <input
                            type="file"
                            ref={gstFileInputRef}
                            style={{ display: "none" }}
                            onChange={(e) => handleFileChange(e, "gst")}
                            disabled={!isGstFetched}
                            accept=".pdf,.jpg,.jpeg,.png"
                        />
                    </div>
                </div>
                <button
                    className="loginBtn"
                    onClick={handleSubmit}

                >
                    {isLoading ? "Processing..." : "PROCEED"}
                </button>

                <button className="closeModalBtn" onClick={() => {
                    resetForm();
                    onClose();
                }}>
                    <RxCross2 />
                </button>
            </div>
        </div>
    );
};

export default VerifyAccountModal;